import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Menu, X, User, LogOut, LayoutDashboard, Plane } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export const Navbar = () => {
  const { user, logout, isAdmin, isStaff } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_72864342-79da-42e1-8892-f4c7769f68b7/artifacts/62md564l_b.png" 
              alt="Circulate Travel" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/trips" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Our Adventures
            </Link>
            <Link to="/resources" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="nav-resources">
              Travel Guides
            </Link>
            <Link to="/contact" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="nav-contact">
              Contact Us
            </Link>
            {user && (
              <Link to="/member" className="text-sm font-medium text-secondary hover:text-secondary/80 transition-colors flex items-center gap-1" data-testid="nav-dashboard">
                <LayoutDashboard className="h-4 w-4" />
                My Dashboard
              </Link>
            )}
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="nav-user-menu">
                    <User className="h-4 w-4" />
                    {user.full_name?.split(' ')[0]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/member')}>
                    <User className="mr-2 h-4 w-4" />
                    Member Dashboard
                  </DropdownMenuItem>
                  {isStaff && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login">
                  Sign In
                </Button>
                <Button onClick={() => navigate('/register')} className="bg-secondary hover:bg-secondary/90 text-white rounded-full" data-testid="nav-register">
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="nav-mobile-toggle"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-border/50">
            <div className="flex flex-col gap-4">
              <Link to="/trips" className="text-sm font-medium text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                Our Adventures
              </Link>
              <Link to="/resources" className="text-sm font-medium text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                Travel Guides
              </Link>
              <Link to="/contact" className="text-sm font-medium text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                Contact Us
              </Link>
              {user ? (
                <>
                  <Link to="/member" className="text-sm font-medium text-secondary hover:text-secondary/80 flex items-center gap-1" onClick={() => setIsOpen(false)}>
                    <LayoutDashboard className="h-4 w-4" />
                    My Dashboard
                  </Link>
                  {isStaff && (
                    <Link to="/admin" className="text-sm font-medium text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                      Admin Panel
                    </Link>
                  )}
                  <button onClick={handleLogout} className="text-sm font-medium text-destructive text-left">
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => { navigate('/login'); setIsOpen(false); }}>
                    Sign In
                  </Button>
                  <Button onClick={() => { navigate('/register'); setIsOpen(false); }} className="bg-secondary hover:bg-secondary/90 text-white">
                    Get Started
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
